// React
import React from 'react';
import { Controller } from 'react-hook-form';
// material-ui components
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
// custom components
import NumberedHeading from '../../../../../shared-components/forms/NumberedHeading';
import EmentorArray from '../shared/EmentorArray';
import JobAideArray from '../shared/JobAideArray';
import LessonFeedbackArray from './components/LessonFeedbackArray';
import ImageAttribution from '../shared/ImageAttribution';

const LessonSummary = (props) => {
  const { control, formState } = props.methods;
  const { errors } = formState;
  const { inputDir } = props;

  return (
    <div>
      <NumberedHeading
        number="1"
        title="Label"
        instructions='Select either "ELO 1", or "ELO 2".'
        required
      />
      <Controller
        control={control}
        name="content.core.label"
        render={({ field }) => (
          <FormControl
            fullWidth
            className="mt-16"
            size="small"
            error={!!errors.content?.core?.label}
            required
          >
            <InputLabel id="select-label">Label</InputLabel>
            <Select
              {...field}
              labelId="select-label"
              id="label"
              label="Label"
              classes={{ select: 'flex items-center space-x-12' }}
              defaultValue=""
            >
              <MenuItem value="" className="space-x-12" disabled>
                Select a label
              </MenuItem>
              <MenuItem value="ELO 1" className="space-x-12">
                ELO 1
              </MenuItem>
              <MenuItem value="ELO 2" className="space-x-12">
                ELO 2
              </MenuItem>
            </Select>
            {!!errors.content?.core?.label ? (
              <FormHelperText>
                {errors.content?.core?.label?.message}
              </FormHelperText>
            ) : null}
          </FormControl>
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="2"
        title="Image"
        instructions="Provide the objective image information."
      />
      <Controller
        control={control}
        name="content.core.media"
        render={({ field }) => (
          <ImageAttribution
            targetObj="content.core.media"
            inputDir={inputDir}
            errors={errors.content?.core?.media}
            {...{ control }}
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="3"
        title="Objective Text (TL)"
        instructions="Restate the objective for this ELO in the Target Language."
      />
      <Controller
        control={control}
        name="content.core.text_tl"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Text (TL)"
            placeholder="Text (TL)"
            id="text"
            error={!!errors.content?.core?.text_tl}
            helperText={errors.content?.core?.text_tl?.message}
            variant="outlined"
            fullWidth
            multiline
            minRows={5}
            maxRows={10}
            required
            dir={inputDir}
          />
        )}
      />

      <NumberedHeading
        number="4"
        title="Objective Text (EN)"
        instructions="Restate the objective for this ELO in English."
      />
      <Controller
        control={control}
        name="content.core.text_en"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Text (EN)"
            placeholder="Text (EN)"
            id="text"
            error={!!errors.content?.core?.text_en}
            helperText={errors.content?.core?.text_en?.message}
            variant="outlined"
            fullWidth
            multiline
            minRows={5}
            maxRows={10}
            required
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="5"
        title="Activity Feedback"
        instructions="Using the controls below, add as many items as needed to provide results feedback for the activities in this ELO."
      />
      <Controller
        control={control}
        name="content.item_feedback"
        render={({ field }) => (
          <LessonFeedbackArray
            inputDir={inputDir}
            {...{ control, errors }}
            materialPages={props.possiblePages}
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="6"
        title="Job Aid Incomplete (TL)"
        instructions="If the learner has not earned the job aid, what message should be shown?"
      />
      <Controller
        control={control}
        name="content.core.no_jobaid_tl"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="No Job Aid"
            placeholder="No Job Aid"
            id="no_jobaid_tl"
            error={!!errors.content?.core?.no_jobaid_tl}
            helperText={errors.content?.core?.no_jobaid_tl?.message}
            variant="outlined"
            fullWidth
            multiline
            minRows={5}
            maxRows={10}
            required
            dir={inputDir}
          />
        )}
      />

      <NumberedHeading
        number="7"
        title="Job Aid Incomplete (EN)"
        instructions="If the learner has not earned the job aid, what message should be shown?"
      />
      <Controller
        control={control}
        name="content.core.no_jobaid_en"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="No Job Aid"
            placeholder="No Job Aid"
            id="no_jobaid_en"
            error={!!errors.content?.core?.no_jobaid_en}
            helperText={errors.content?.core?.no_jobaid_en?.message}
            variant="outlined"
            fullWidth
            multiline
            minRows={5}
            maxRows={10}
            required
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="8"
        title="Job Aid Complete (TL)"
        instructions="If the learner has earned the job aid, what message should be shown?"
      />
      <Controller
        control={control}
        name="content.core.yes_jobaid_tl"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Yes Job Aid"
            placeholder="Yes Job Aid"
            id="yes_jobaid_tl"
            error={!!errors.content?.core?.yes_jobaid_tl}
            helperText={errors.content?.core?.yes_jobaid_tl?.message}
            variant="outlined"
            fullWidth
            multiline
            minRows={5}
            maxRows={10}
            required
            dir={inputDir}
          />
        )}
      />

      <NumberedHeading
        number="9"
        title="Job Aid Complete (EN)"
        instructions="If the learner has earned the job aid, what message should be shown?"
      />
      <Controller
        control={control}
        name="content.core.yes_jobaid_en"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Yes Job Aid"
            placeholder="Yes Job Aid"
            id="yes_jobaid_en"
            error={!!errors.content?.core?.yes_jobaid_en}
            helperText={errors.content?.core?.yes_jobaid_en?.message}
            variant="outlined"
            fullWidth
            multiline
            minRows={5}
            maxRows={10}
            required
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="10"
        title="Job Aid Lead (TL)"
        instructions="Enter a lead sentence for the Job Aide in the target language."
      />
      <Controller
        control={control}
        name="content.core.jobaid_pdf_lead_tl"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Job Aid Lead (TL)"
            placeholder="Job Aid Lead (TL)"
            id="jobaid_pdf_lead_tl"
            error={!!errors.content?.core?.jobaid_pdf_lead_tl}
            helperText={errors.content?.core?.jobaid_pdf_lead_tl?.message}
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            dir={inputDir}
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="11"
        title="Job Aid Lead (EN)"
        instructions="Enter a lead sentence for the Job Aide in English."
      />
      <Controller
        control={control}
        name="content.core.jobaid_pdf_lead_en"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Job Aid Lead (EN)"
            placeholder="Job Aid Lead (EN)"
            id="jobaid_pdf_lead_en"
            error={!!errors.content?.core?.jobaid_pdf_lead_en}
            helperText={errors.content?.core?.jobaid_pdf_lead_en?.message}
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
          />
        )}
      />

      <NumberedHeading
        number="12"
        title="Job Aid PDF Content"
        instructions="What is the content that should be in the Job Aid?"
      />
      <Controller
        control={control}
        name="content.jobaid_pdf"
        render={({ field }) => <JobAideArray {...{ control, errors }} />}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="13"
        title="eMentor Notes"
        instructions="Using the controls below, add any supplemental content to this page"
      />
      <Controller
        control={control}
        name="content.eMentor"
        render={({ field }) => <EmentorArray {...{ control, errors }} />}
      />
    </div>
  );
};

export default LessonSummary;
